/**
 * DuplicateOrigin — 重复号码"首次成功来源"的统一展示
 *
 * 数据来自服务端 originBatch（见 server/ocr-origin.js），关键字段：
 *   crossUser  true=和别的用户撞号 / false=同一用户自己传重 / null=来源用户未知（历史台账）
 *   canView    查看者是否有权看到来源用户身份（无权时姓名/批次/图片都是 null）
 *   sameBatch  来源就是这张图所在的批次（同批第 3 张起的多余重复图）
 *   imagesCleared  来源批次的图片已被导出清盘，仅台账留有记录
 *
 * 之前的问题：管理员端的 isSameUser 是写死的 true，不论是不是同一个人都只显示"日期 · 第几批"，
 * 无法分辨"自己传重了"和"和别人撞号"；图片管理页的角标只有"重复"两个字；
 * 普通用户的批次详情里重复图直接消失，看不到任何提示。
 *
 * 导出：
 *   describeOrigin(origin, audience)  纯函数，返回文案与语气
 *   dupBadge(origin)                  图片网格角标（文字 + 颜色）
 *   OriginLine                        一行说明（+ 可选操作）
 *   OriginModal                       点击角标弹出的来源卡
 */
import React from 'react';
import Modal from './Modal';

const TONE = {
  danger: { color: 'var(--c-danger)', bg: 'rgba(255,59,48,.07)', border: 'var(--c-danger)' },
  warn:   { color: 'var(--c-accent)', bg: 'rgba(255,149,0,.07)', border: 'var(--c-accent)' },
  muted:  { color: 'var(--c-text-secondary)', bg: 'var(--c-bg)', border: 'var(--c-border)' },
};

/**
 * @param origin    服务端 originBatch，可为 null
 * @param audience  'admin'（管理员/代理，以被查看用户为主语）或 'user'（用户本人）
 * @returns { tone, tag, headline, detail, crossUser }
 */
export function describeOrigin(origin, audience = 'admin') {
  if (!origin) {
    return { tone: 'muted', tag: '重复', crossUser: null,
      headline: '已在其他批次配对', detail: '本次重复不计入成功' };
  }

  const where = [origin.uploadDate, origin.seq != null ? `第${origin.seq}批` : null].filter(Boolean).join(' · ');
  const cleared = origin.imagesCleared ? '（来源图片已清理）' : '';

  if (origin.sameBatch) {
    return { tone: 'warn', tag: '重复', crossUser: false,
      headline: '本批次已成功配对', detail: '这张是多余的重复图，不计入成功' };
  }

  if (origin.crossUser === true) {
    if (origin.canView && origin.userName) {
      return { tone: 'danger', tag: '重复·他人', crossUser: true,
        headline: `已由 ${origin.userName} 成功提交`,
        detail: `${where || '时间未知'}${cleared}，本次重复不计入成功` };
    }
    // 无权查看他人身份：只告知"被其他用户提交过"，不透露是谁、哪一批
    return { tone: 'danger', tag: '重复·他人', crossUser: true,
      headline: '该号码已被其他用户提交',
      detail: `${origin.uploadDate ? origin.uploadDate + '，' : ''}本次重复不计入成功` };
  }

  if (origin.crossUser === false) {
    return { tone: 'warn', tag: '重复·本人', crossUser: false,
      headline: audience === 'user' ? '你已成功提交过这个号码' : '该用户已提交过这个号码',
      detail: `${where || '时间未知'}${cleared}，本次重复不计入成功` };
  }

  // crossUser === null：历史强制登记的台账，来源用户未知
  return { tone: 'muted', tag: '重复·历史', crossUser: null,
    headline: '该号码此前已成功提交',
    detail: `${origin.uploadDate ? origin.uploadDate + '，' : ''}来源批次已清理，本次重复不计入成功` };
}

// 图片网格角标：跨人撞号用红色，一眼能和"自己传重"的橙色区分
export function dupBadge(origin) {
  const d = describeOrigin(origin, 'admin');
  return {
    text: d.tag,
    background: d.tone === 'danger' ? 'rgba(255,59,48,0.9)'
              : d.tone === 'warn'   ? 'rgba(255,149,0,0.85)'
              : 'rgba(142,142,147,0.9)',
    title: `${d.headline}。${d.detail}（点击查看来源）`,
  };
}

const linkBtn = {
  fontSize: 11, padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
  border: '1px solid var(--c-border)', background: 'var(--c-surface)', color: 'var(--c-primary)',
};

/**
 * 一行来源说明。onOpenImage(origin.image) / onGoUser(origin.userId) 传了才显示对应按钮。
 */
export function OriginLine({ origin, audience = 'admin', onOpenImage, onGoUser }) {
  const d = describeOrigin(origin, audience);
  const t = TONE[d.tone];
  const canOpen = !!(origin && origin.image && onOpenImage);
  const canGo   = !!(origin && origin.crossUser === true && origin.canView && origin.userId != null && onGoUser);
  return (
    <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
      <span>
        <strong style={{ color: t.color }}>{d.headline}</strong>
        <span>。{d.detail}</span>
      </span>
      {canOpen && <button type="button" style={linkBtn} onClick={() => onOpenImage(origin.image)}>看首次成功的图</button>}
      {canGo && <button type="button" style={linkBtn} onClick={() => onGoUser(origin.userId)}>查看该用户</button>}
    </div>
  );
}

/**
 * 点击图片网格的"重复"角标后弹出的来源卡。img 需含 ocrPhone / originBatch / thumbUrl / url。
 */
export function OriginModal({ img, audience = 'admin', onClose, onOpenImage, onGoUser }) {
  if (!img) return null;
  const origin = img.originBatch || null;
  const d = describeOrigin(origin, audience);
  const t = TONE[d.tone];
  const canGo = !!(origin && origin.crossUser === true && origin.canView && origin.userId != null && onGoUser);
  return (
    <Modal show={true} onClose={onClose} title="重复号码来源">
      <div style={{ padding: '2px 0 6px' }}>
        <div style={{
          fontSize: 18, fontWeight: 700, letterSpacing: '.06em',
          fontVariantNumeric: 'tabular-nums', marginBottom: 10,
        }}>
          {String(img.ocrPhone || '').replace(/\D/g, '') || '—'}
        </div>

        <div style={{ border: `1px solid ${t.border}`, background: t.bg, borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: t.color }}>{d.headline}</div>
          <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 3 }}>{d.detail}</div>
        </div>

        {origin && origin.image && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12 }}>
            <img
              src={origin.image.thumbUrl || origin.image.url} alt=""
              onClick={() => onOpenImage && onOpenImage(origin.image)}
              style={{ width: 52, height: 92, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--c-border)', cursor: 'zoom-in' }}
              onError={e => { e.target.style.display = 'none'; }}
            />
            <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)' }}>
              首次成功的那张图。点击放大，与当前这张对比，可判断是同一张截图重复上传，还是两个人各自加了同一个客户。
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn btn-outline" style={{ flex: 1, height: 44 }} onClick={onClose}>关闭</button>
          {canGo && (
            <button className="btn btn-primary" style={{ flex: 1, height: 44 }} onClick={() => onGoUser(origin.userId)}>
              查看该用户的图片
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
